import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { useContrastText } from "@/hooks/useContrastText"
import { cn, getCssVarValue, hslStringToHex } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[transform,opacity] duration-200 ease-in-out focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-sm focus-visible:shadow-primary/20 disabled:pointer-events-none disabled:opacity-50 transform active:scale-95 [touch-action:manipulation] min-h-[44px] md:min-h-0",
  {
    variants: {
      variant: {
        default: "bg-primary shadow-sm hover:bg-primary/90",
        destructive:
          "bg-destructive shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 md:h-10 px-4 py-2",
        sm: "h-10 md:h-9 rounded-md px-3",
        lg: "h-12 md:h-11 rounded-md px-8",
        icon: "h-11 w-11 md:h-10 md:w-10 min-w-[44px] md:min-w-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const { getContrastForColor } = useContrastText()
    const [dynamicTextColorClass, setDynamicTextColorClass] = React.useState("")

    React.useLayoutEffect(() => {
      let cssVarName: string | null = null
      
      // Determine the effective variant, defaulting to 'default' if not provided.
      const effectiveVariant = variant || 'default';

      switch(effectiveVariant) {
        case "default":
          cssVarName = '--primary'
          break
        case "destructive":
          cssVarName = '--destructive'
          break
        case "secondary":
          cssVarName = '--secondary'
          break
        default:
          // For other variants ('outline', 'ghost', 'link'), no dynamic color is needed.
          setDynamicTextColorClass("")
          return
      }

      const hslString = getCssVarValue(cssVarName)
      if (hslString) {
        const hexColor = hslStringToHex(hslString)
        const contrast = getContrastForColor(hexColor)
        setDynamicTextColorClass(contrast === 'white' ? 'text-white' : 'text-black')
      } else {
        // Fallback for SSR or if the CSS variable isn't available.
        if (effectiveVariant === 'default' || effectiveVariant === 'destructive') {
            setDynamicTextColorClass('text-white')
        } else {
            setDynamicTextColorClass('text-black')
        }
      }
    }, [variant, getContrastForColor])
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), dynamicTextColorClass)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
